import { Button, Modal, Switch, TextField } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import SaveIcon from "@material-ui/icons/Save";
import { useMutation, useQueryClient } from "react-query";
import { ReactComponent as LoadingIcon } from "../../assets/svgs/loading-icon.svg";
import { toast } from "react-toastify";
import {
  deleteCategory,
  updateCategory,
} from "../../services/category.service";
import CategoryFieldList from "./CategoryFieldList";
import Backdrop from "@material-ui/core/Backdrop";
import Fade from "@material-ui/core/Fade";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import CategoryTypeList from "./CategoryTypeList";

interface Props {
  open: boolean;
  handleClose: () => void;
  detail: {
    id: number;
    name: string;
    description: string | null;
    isActive: boolean;
    updatedAt: string;
    createdAt: string;
    fields: { id?: number; name: string; categoryId?: number }[];
    types: { id?: number; name: string; categoryId?: number }[];
    _count: { products: number };
  } | null;
}

const EditCategoryModal: React.FC<Props> = ({ open, handleClose, detail }) => {
  const [errTarget, setErrTarget] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<{ name: string; id?: number }[]>([]);
  const [types, setTypes] = useState<{ name: string; id?: number }[]>([]);
  const [isActive, setIsActive] = useState(false);
  const [delModal, setDelModal] = useState(false);

  const queryClient = useQueryClient();
  const { isLoading, mutate } = useMutation(updateCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries("categories");
      handleClose();
    },
    onError: (err: any) => {
      const field = err.response.data.errorField ? true : false;
      let message;
      if (field) {
        message = "Category has already been created !";
        setErrTarget("name");
      }
      toast.error(`${message ? message : "Technical Fault, contact Admin !"}`, {
        autoClose: 2200,
      });
    },
  });

  useEffect(() => {
    setName("");
    setDescription("");
    setFields([]);
    setIsActive(true);
    if (detail) {
      setName((detail && detail.name) || "");
      setDescription((detail && detail.description) || "");
      setFields((detail && detail.fields) || []);
      setTypes((detail && detail.types) || []);
      setIsActive((detail && detail.isActive) || false);
    }
  }, [detail]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const newFields = fields.filter((f) => !("id" in f));
    const newTypes = types.filter((t) => !("id" in t));

    mutate({
      id: detail && detail.id,
      catData: {
        name,
        description,
        fields: newFields,
        types: newTypes,
        isActive,
      },
    });
  };

  const { isLoading: isDelLoad, mutate: mutateDel } = useMutation(
    deleteCategory,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("categories");
        handleClose();
      },
      onError: () => {
        toast.error(`Technical Fault, contact Admin !`, {
          autoClose: 2200,
        });
      },
    }
  );

  const handleDelete = () => {
    console.log(detail);
    setDelModal(false);
    if (detail && detail.id) mutateDel(detail.id);
  };

  return (
    <div>
      <ConfirmDeleteModal
        delFunc={handleDelete}
        open={delModal}
        handleClose={() => setDelModal(false)}
      />
      <Modal
        onClose={handleClose}
        open={open}
        className="flex justify-center items-center"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={open}>
          <form
            onSubmit={handleSubmit}
            style={{ width: "850px" }}
            className="bg-white rounded-md flex flex-col w-full focus:outline-none"
          >
            <div className="flex-1 bg-white w-full rounded-t-md p-5">
              <div className="flex items-center justify-between">
                <p className="text-blue-500 font-medium text-2xl">
                  Edit Category
                </p>
                <div className="flex items-center">
                  <p
                    className={`font-medium mr-1 ${
                      isActive ? "text-blue-500" : "text-red-500"
                    }`}
                  >
                    {isActive ? "Active" : "Inactive"}
                  </p>
                  <Switch
                    color="primary"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </div>
              </div>
              <div className="pt-5 w-full space-y-5">
                <TextField
                  inputProps={{ maxLength: 191 }}
                  id="name"
                  label="Category Name"
                  variant="outlined"
                  className="w-full"
                  autoFocus
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={errTarget === "name"}
                />
                <TextField
                  inputProps={{ maxLength: 500 }}
                  className="w-full"
                  id="description"
                  label="Description"
                  multiline
                  rows={3}
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <div className="flex w-full space-x-5">
                  <CategoryTypeList types={types} setTypes={setTypes} />
                  <CategoryFieldList fields={fields} setFields={setFields} />
                </div>
              </div>
            </div>
            <div className="h-16 w-full bg-gray-100 rounded-b-md flex space-x-4 justify-between items-center px-5">
              <Button
                onClick={() => setDelModal(true)}
                color="secondary"
                variant="contained"
                className="focus:outline-none"
              >
                {isDelLoad ? (
                  <LoadingIcon className="text-white animate-spin h-5 w-5" />
                ) : (
                  "Delete"
                )}
              </Button>
              <div className="space-x-5">
                <Button
                  onClick={handleClose}
                  color="primary"
                  variant="contained"
                  className="focus:outline-none"
                >
                  Cancel
                </Button>
                <Button
                  disabled={isLoading}
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="focus:outline-none"
                  startIcon={<SaveIcon />}
                >
                  {isLoading ? (
                    <LoadingIcon className="text-blue-600 animate-spin h-5 w-5" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </div>
            </div>
          </form>
        </Fade>
      </Modal>
    </div>
  );
};

export default EditCategoryModal;
